import { useEffect, useState } from "react";
import { api, formatApiError, APP_STATUS_LABELS } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { Modal } from "@/pages/mentor/MentorSessions";
import { toast } from "sonner";
import { Loader2, Award, CheckCircle2, XCircle, ShieldCheck } from "lucide-react";

const CERT_KIND_LABELS = {
  course_completion: "شهادة إتمام",
  association_appreciation: "شهادة شكر وتقدير للجمعية",
  training_completion: "شهادة حضور وإتمام التدريب التعاوني",
  training_excellence: "شهادة امتياز في التدريب التعاوني",
};
const certLabel = (cert) => cert.kind_label || CERT_KIND_LABELS[cert.kind || "course_completion"] || "شهادة";
const certBeneficiary = (cert) => cert.student_name || cert.beneficiary_name || cert.association_name || "—";

export default function CertificatesAdmin() {
  const [certs, setCerts] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showIssue, setShowIssue] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [checking, setChecking] = useState(false);
  const [issuing, setIssuing] = useState(false);
  const [forceOverride, setForceOverride] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [c, apps] = await Promise.all([api.get("/certificates"), api.get("/applications")]);
      setCerts(c.data.certificates || []);
      setApplications((apps.data.applications || []).filter((a) => a.status === "accepted" || a.status === "assigned"));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openIssue = () => { setSelectedApp(null); setChecklist(null); setForceOverride(false); setShowIssue(true); };

  const checkEligibility = async (appId) => {
    setChecking(true);
    setChecklist(null);
    try {
      const { data } = await api.get(`/certificates/eligibility/${appId}`);
      setChecklist(data.checklist);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setChecking(false); }
  };

  const issue = async () => {
    if (!selectedApp) return;
    setIssuing(true);
    try {
      await api.post("/certificates", { application_id: selectedApp.id, force: forceOverride });
      toast.success("تم إصدار الشهادة");
      setShowIssue(false);
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (typeof detail === "object" && detail?.checklist) {
        setChecklist(detail.checklist);
        toast.error(detail.message || "الطالب غير مؤهل بعد");
      } else {
        toast.error(formatApiError(e));
      }
    } finally { setIssuing(false); }
  };

  return (
    <DashboardLayout>
      <div data-testid="certificates-admin">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-teal-700">إدارة الشهادات</div>
            <h1 className="mt-2 text-2xl font-heading font-black text-slate-900">جميع الشهادات المُصدرة</h1>
            <p className="mt-2 text-sm text-slate-600">تُجهز المسودة بعد إكمال التدريب وتسجيل 40 ساعة والتقييم النهائي، ثم تعتمدها الجمعية بختمها.</p>
          </div>
          <div className="rounded-xl border border-teal-100 bg-teal-50 px-4 py-3 text-sm text-teal-900">
            تصدر شهادات التدريب التعاوني تلقائيًا من رحلة الطالب.
          </div>
        </div>
        {loading ? (
          <div className="grid place-items-center py-16"><Loader2 className="h-8 w-8 animate-spin text-teal-700" /></div>
        ) : (
          <div className="bg-white rounded-2xl border border-cream-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-cream-50">
                  <tr>
                    <th className="text-start px-4 py-3 font-semibold text-slate-700">رقم الشهادة</th>
                    <th className="text-start px-4 py-3 font-semibold text-slate-700">النوع</th>
                    <th className="text-start px-4 py-3 font-semibold text-slate-700">المستفيد</th>
                    <th className="text-start px-4 py-3 font-semibold text-slate-700">الفرصة</th>
                    <th className="text-start px-4 py-3 font-semibold text-slate-700">الجمعية</th>
                    <th className="text-start px-4 py-3 font-semibold text-slate-700">الساعات</th>
                    <th className="text-start px-4 py-3 font-semibold text-slate-700">الحالة</th>
                    <th className="text-start px-4 py-3 font-semibold text-slate-700">التاريخ</th>
                    <th className="text-start px-4 py-3 font-semibold text-slate-700">إجراء</th>
                  </tr>
                </thead>
                <tbody>
                  {certs.map((c) => (
                    <tr key={c.id} className="border-t border-cream-100 hover:bg-cream-50/50" data-testid={`cert-row-${c.id}`}>
                      <td className="px-4 py-3 font-mono text-xs" dir="ltr">{c.cert_id}</td>
                      <td className="px-4 py-3 text-slate-700">{certLabel(c)}</td>
                      <td className="px-4 py-3 font-semibold text-slate-900">{certBeneficiary(c)}</td>
                      <td className="px-4 py-3 text-slate-700">{c.program_name || "—"}</td>
                      <td className="px-4 py-3 text-slate-700">{c.association_name || "—"}</td>
                      <td className="px-4 py-3 text-slate-700 arabic-numerals">{c.hours ?? "—"}</td>
                      <td className="px-4 py-3"><span className={`rounded-full px-2 py-1 text-xs font-bold ${c.status === "pending_association_approval" ? "bg-amber-50 text-amber-800" : "bg-emerald-50 text-emerald-700"}`}>{c.status === "pending_association_approval" ? "بانتظار اعتماد الجمعية" : "صادرة"}</span></td>
                      <td className="px-4 py-3 text-slate-600">{new Date(c.issued_at).toLocaleDateString("ar-SA")}</td>
                      <td className="px-4 py-3">
                        <a href={`/verify/${c.cert_id}`} target="_blank" rel="noreferrer" className="text-teal-700 hover:underline text-xs font-semibold">تحقق</a>
                      </td>
                    </tr>
                  ))}
                  {certs.length === 0 && (
                    <tr><td colSpan={9} className="p-12 text-center text-slate-500">لا توجد شهادات مُصدرة بعد</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {showIssue && (
          <Modal onClose={() => setShowIssue(false)} title="إصدار شهادة — فحص الأهلية">
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">اختر طلب الطالب (فرصة تدريب تعاوني)</label>
                <select
                  value={selectedApp?.id || ""}
                  onChange={(e) => {
                    const a = applications.find((x) => x.id === e.target.value);
                    setSelectedApp(a || null);
                    setChecklist(null);
                    if (a) checkEligibility(a.id);
                  }}
                  className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3"
                  data-testid="cert-app-select"
                >
                  <option value="">— اختر —</option>
                  {applications.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.student_name} · {a.opportunity?.title || "فرصة"} · {APP_STATUS_LABELS[a.status] || a.status}
                    </option>
                  ))}
                </select>
              </div>

              {checking && <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> جارٍ فحص الأهلية...</div>}

              {checklist && (
                <div className={`p-4 rounded-2xl border ${checklist.eligible ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`} data-testid="cert-checklist">
                  <div className="flex items-center gap-2 font-heading font-bold text-slate-900 mb-3">
                    <ShieldCheck className="h-5 w-5 text-teal-700" />
                    {checklist.eligible ? "الطالب مؤهّل لإصدار الشهادة ✓" : "الطالب غير مؤهّل بعد"}
                  </div>
                  <ul className="space-y-2 text-sm" data-testid="cert-checklist-items">
                    {[
                      ["opportunity_completed", "إكمال التدريب"],
                      ["task_hours", "40 ساعة من المهام المكتملة"],
                      ["final_evaluation", "التقييم النهائي"],
                    ].map(([k, label]) => (
                      <li key={k} className="flex items-start gap-2" data-testid={`checklist-${k}`}>
                        {checklist[k]?.ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5" /> : <XCircle className="h-4 w-4 text-red-500 mt-0.5" />}
                        <div className="flex-1">
                          <div className="font-semibold text-slate-800">{label}</div>
                          <div className="text-xs text-slate-600">{checklist[k]?.note}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                  {!checklist.eligible && (
                    <label className="mt-4 flex items-center gap-2 text-xs text-amber-800">
                      <input type="checkbox" checked={forceOverride} onChange={(e) => setForceOverride(e.target.checked)} data-testid="force-issue" />
                      <span>تجاوز الفحص (مسؤولية مدير النظام)</span>
                    </label>
                  )}
                </div>
              )}
            </div>
            <div className="mt-6 flex gap-3">
              <button onClick={() => setShowIssue(false)} className="flex-1 px-5 py-3 rounded-lg text-slate-700 font-medium hover:bg-slate-100">إلغاء</button>
              <button
                onClick={issue}
                disabled={issuing || !selectedApp || (!checklist?.eligible && !forceOverride)}
                className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-teal-700 text-white font-semibold hover:bg-teal-600 disabled:opacity-40"
                data-testid="save-cert-btn"
              >
                {issuing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Award className="h-4 w-4" />}
                إصدار الشهادة
              </button>
            </div>
          </Modal>
        )}
      </div>
    </DashboardLayout>
  );
}
